/**
 * Browser-download helper (M7): wraps the `Blob` + object URL + `<a
 * download>` click pattern in one place so every exporter triggers a
 * download the same way, and the object URL is always revoked afterward
 * rather than leaking. Deliberately tiny and DOM-only -- this is exactly
 * the kind of browser-only concern that belongs in `apps/web`, never in
 * `packages/fit-core`.
 */
export function downloadBytes(data: Uint8Array | string, fileName: string, mimeType: string): void {
  // `Uint8Array`'s generic `ArrayBufferLike` buffer type is wider than
  // the DOM lib's `BlobPart` requirement (`ArrayBufferView<ArrayBuffer>`
  // specifically); the value itself is always `ArrayBuffer`-backed in
  // every real call site here (bytes from `fit-core`'s encoders).
  // `ConstructorParameters<typeof Blob>[0]` gets the correctly-typed
  // first argument without naming `BlobPart` directly -- it's a type
  // with no runtime global, which this repo's non-type-aware `no-undef`
  // lint rule can't otherwise distinguish from a genuinely undefined
  // identifier.
  const blob = new Blob([data] as unknown as ConstructorParameters<typeof Blob>[0], {
    type: mimeType,
  })
  const url = URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = fileName
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  } finally {
    // Revoking synchronously (rather than after a timeout) is safe --
    // browsers resolve the download from the already-triggered
    // navigation/click before running any further synchronous JS, and
    // every real browser's docs recommend revoking as soon as the
    // triggering click has happened.
    URL.revokeObjectURL(url)
  }
}

/** The non-ZIP, single-activity export formats this app supports (M7). */
export type ExportFormat = 'fit' | 'gpx' | 'tcx' | 'csv' | 'geojson'

/** MIME types for each export format, plus the ZIP bundle format used for batch/multi-segment downloads. */
export const EXPORT_MIME_TYPES: Record<ExportFormat | 'zip', string> = {
  fit: 'application/octet-stream',
  gpx: 'application/gpx+xml',
  tcx: 'application/vnd.garmin.tcx+xml',
  csv: 'text/csv',
  geojson: 'application/geo+json',
  zip: 'application/zip',
}
